import { useRef, useState, useEffect } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { MessageCircle, ArrowDown, Star } from "lucide-react";
import { HERO_IMG, waLink, api } from "../../lib/site";

const lines = [
  { text: "Explore Nagaland" },
  { text: "On Your ", accent: "Own" },
  { accent: "Terms." },
];

const lineVariant = {
  hidden: { y: "110%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 0.9, delay: 0.25 + i * 0.12, ease: [0.76, 0, 0.24, 1] },
  }),
};

export default function Hero() {
  const ref = useRef(null);
  const [tilt, setTilt] = useState({ x: 0, y: 0 });
  const [summary, setSummary] = useState(null);
  useEffect(() => {
    api.get("/reviews/summary").then((r) => setSummary(r.data)).catch(() => {});
  }, []);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1.1, 1.25]);

  const onMove = (e) => {
    const { innerWidth, innerHeight } = window;
    setTilt({ x: (e.clientX / innerWidth - 0.5) * 18, y: (e.clientY / innerHeight - 0.5) * 18 });
  };

  return (
    <section
      id="home"
      ref={ref}
      onMouseMove={onMove}
      data-testid="hero-section"
      className="relative flex min-h-screen items-end overflow-hidden"
    >
      <motion.div
        style={{ y: bgY, scale: bgScale, x: tilt.x }}
        transition={{ type: "spring", stiffness: 40, damping: 20 }}
        className="absolute inset-0 z-0"
      >
        <img src={HERO_IMG} alt="Winding mountain road through Nagaland" className="h-full w-full object-cover" />
      </motion.div>
      <div className="absolute inset-0 z-[1] bg-gradient-to-t from-ink via-ink/50 to-ink/40" />
      <div className="absolute inset-0 z-[1] bg-ink/30" />

      <div className="relative z-10 mx-auto w-full max-w-[1400px] px-5 pb-16 pt-32 md:px-10 md:pb-24">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.15 }}
          className="mb-6 flex items-center gap-3"
        >
          <span className="h-px w-10 bg-volt" />
          <span className="text-xs font-bold uppercase tracking-[0.25em] text-volt">
            Self-Drive Rentals · Kohima, Nagaland
          </span>
        </motion.div>

        <h1 className="font-heading text-5xl font-black uppercase leading-[0.9] tracking-tighter text-white sm:text-7xl md:text-8xl lg:text-[8.5rem]">
          {lines.map((line, i) => (
            <span key={i} className="line-mask">
              <motion.span
                custom={i}
                variants={lineVariant}
                initial="hidden"
                animate="show"
                className="block"
              >
                {line.text}
                {line.accent && (
                  <span className="font-serif-ed font-medium italic tracking-normal text-volt">
                    {line.accent}
                  </span>
                )}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9, duration: 0.7 }}
          className="mt-8 max-w-xl text-base font-medium leading-relaxed text-white/75 md:text-lg"
        >
          Premium self-drive cars and bikes available for daily & long-term rentals in Kohima.
          Clean, serviced and ready for the mountains.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.05, duration: 0.7 }}
          className="mt-10 flex flex-col gap-3 sm:flex-row"
        >
          <a
            href={waLink()}
            target="_blank"
            rel="noreferrer"
            data-testid="hero-inquire-button"
            className="group inline-flex items-center justify-center gap-2 bg-volt px-8 py-4 text-sm font-bold uppercase tracking-wider text-ink transition-colors hover:bg-volt-hover"
          >
            <MessageCircle size={17} /> Inquire on WhatsApp
          </a>
          <a
            href="#vehicles"
            data-testid="hero-view-vehicles-button"
            className="inline-flex items-center justify-center gap-2 border border-white/30 px-8 py-4 text-sm font-bold uppercase tracking-wider text-white backdrop-blur-sm transition-colors hover:border-volt hover:text-volt"
          >
            View Available Vehicles
          </a>
        </motion.div>

        {summary && summary.count > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.25, duration: 0.7 }}
            className="mt-8 inline-flex items-center gap-3"
            data-testid="hero-rating-summary"
          >
            <div className="flex">
              {[1, 2, 3, 4, 5].map((n) => (
                <Star key={n} size={16} className={n <= Math.round(summary.average) ? "fill-volt text-volt" : "text-white/30"} />
              ))}
            </div>
            <span className="text-sm text-white/80">
              <span className="font-bold text-white">{summary.average.toFixed(1)}</span>
              <span className="mx-1.5 text-white/30">·</span>
              Rated by {summary.count} rider{summary.count === 1 ? "" : "s"}
            </span>
          </motion.div>
        )}
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4 }}
        className="absolute bottom-6 right-5 z-10 hidden items-center gap-2 text-white/50 md:right-10 md:flex"
      >
        <ArrowDown size={16} className="animate-bounce" />
        <span className="text-[10px] font-bold uppercase tracking-[0.2em]">Scroll to explore</span>
      </motion.div>
    </section>
  );
}
